/**
 * CARK · LIDERLIK · DAVET — API ile arayuzun paylastigi sozlesme (F-GAM-17/18/19, oyunlastirma dalga 2 surum 7b).
 *
 * Tasarim: dalga-2-tasarim.md §1.4–1.6, §3.3–3.5, §4 ve §10 EMRE KARARLARI (07.10.2026). Arayuzun yerel gecici
 * tipleri (`apps/ogrenci/src/oyun2-tipleri.ts`, `apps/panel/src/oyun2-tipleri.ts`) bu dosyadaki AYNI ADLI tiplere
 * baglanir; alan adlari onlarla birebirdir.
 *
 * Burada yalniz BICIM dogrulanir. Hak, sonuc (cekilis), olasilik, siralama, sinif, odul miktari ve bekleme SUNUCUDA,
 * kisi kilidi altinda karara baglanir. Istemci puan, dilim, sinif, ogrenci kimligi ya da miktar GONDEREMEZ (semalar
 * `strict`: fazla alan reddedilir).
 *
 * COCUK GUVENLIGI (Emre 07.10.2026 "CARK TASARIMI"): OLASILIK OGRENCIYE DONMEZ. Ogrenci yanitinda dilim yalniz
 * `{ puan }`; agirlik ve olasilik metni yalniz yonetim yanitinda (`oyun.cark.yonet`).
 */
import { z } from "zod";
import { GEREKCE_AGIR_EN_AZ, gerekceSemasi } from "./gerekce";

// =========================================================================== ortak

const istekKimligi = z.string().uuid();

/** Hafta: Pazartesi, "YYYY-MM-DD" (Europe/Istanbul takvim gunu). */
export const haftaGunuSemasi = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, "Hafta YYYY-AA-GG biçiminde olmalı.")
  .refine(
    (g) => {
      const t = new Date(`${g}T00:00:00.000Z`);
      return (
        !Number.isNaN(t.getTime()) && t.toISOString().slice(0, 10) === g && t.getUTCDay() === 1
      );
    },
    { message: "Hafta bir Pazartesi olmalı." },
  );

/** Sinif duzeyi — veritabani `GradeLevel` ile ayni (liderlik kapsami; tutarlilik testi `oyun-b.test.ts`). */
export const SINIF_DUZEYLERI = [
  "G5",
  "G6",
  "G7",
  "G8",
  "G9",
  "G10",
  "G11",
  "G12",
  "GRADUATE",
  "OTHER",
] as const;
export type SinifDuzeyi = (typeof SINIF_DUZEYLERI)[number];

// =========================================================================== CARK (F-GAM-19)

/** Bir tablodaki dilim sayisi. Ust sinir ekranin cizebildiginden (istemci duzeni) degil, makul tablodan. */
export const CARK_DILIM_EN_AZ = 2;
export const CARK_DILIM_EN_COK = 16;
/** Tek dilimin en buyuk puani — yanlis girilen bir sifirin (50.000) ekonomiyi bozmasina karsi. */
export const CARK_PUAN_EN_COK = 100_000;
/**
 * Toplam agirlik ust siniri. `crypto.randomInt(max)` 2^48'e kadar calisir; veritabaninda `INTEGER` (2^31-1). Bir
 * milyar ikisinin de altinda ve 1/20.000 gibi oranlari tam sayiyla ifade etmeye fazlasiyla yeter.
 */
export const CARK_TOPLAM_AGIRLIK_EN_COK = 1_000_000_000;
export const CARK_BEKLEME_SAAT_EN_AZ = 1;
export const CARK_BEKLEME_SAAT_EN_COK = 168;

/**
 * Tablo dilimi: puan (> 0) ve TAM SAYI agirlik (>= 0). Sifir agirlikli dilim tabloda durur ama HIC secilmez
 * (`cark-sec.ts`). Ondalik agirlik yok: olasilik = agirlik / toplam, kayan nokta toplamasi tabloyu bozmasin.
 */
export const carkTabloDilimiSemasi = z
  .object({
    puan: z.number().int().positive().max(CARK_PUAN_EN_COK),
    agirlik: z.number().int().min(0).max(CARK_TOPLAM_AGIRLIK_EN_COK),
  })
  .strict();
export type CarkTabloDilimi = z.infer<typeof carkTabloDilimiSemasi>;

/**
 * Tablo: puana gore KESIN ARTAN (puan tekil — bir dilim = bir puan degeri; `segmentIndex` <-> puan bire bir), en az
 * bir dilim secilebilir, toplam ust sinirin altinda. Veritabani tetikleyicisi (`cark_tablosu_dogrula`) AYNI kurallari
 * yeniden zorlar; bu sema veritabanindan okunan JSON icin de kullanilir (okunan JSON'a guvenilmez).
 */
export const carkDilimleriSemasi = z
  .array(carkTabloDilimiSemasi)
  .min(CARK_DILIM_EN_AZ, `Çarkta en az ${CARK_DILIM_EN_AZ} dilim olmalı.`)
  .max(CARK_DILIM_EN_COK, `Çarkta en çok ${CARK_DILIM_EN_COK} dilim olabilir.`)
  .refine((d) => d.every((x, i) => i === 0 || x.puan > d[i - 1]!.puan), {
    message: "Dilimler puana göre küçükten büyüğe sıralı ve puanlar tekil olmalı.",
  })
  .refine((d) => d.reduce((t, x) => t + x.agirlik, 0) > 0, {
    message: "En az bir dilimin ağırlığı sıfırdan büyük olmalı.",
  })
  .refine((d) => d.reduce((t, x) => t + x.agirlik, 0) <= CARK_TOPLAM_AGIRLIK_EN_COK, {
    message: "Toplam ağırlık çok büyük.",
  });

/**
 * TABLO SURUMU 1 — Emre 07.10.2026 ~02:50 "CARK ORANLARI" (§10, §9'un yerine gecer). Toplam 1.340.000:
 *   5 %60 · 10 %27 · 20 %10 · 50 %2,9 · 500 = 1/1.250 · 1.000 = 1/6.700 · 5.000 = 1/20.000.
 * Toplam 6.700 ve 20.000'in ortak katlarindan secildi: 1/6.700 = 200, 1/20.000 = 67, 1/1.250 = 1.072 TAM. Hedef
 * yuzdelerin toplami %99,999925; kalan 1 birim (%0,0000746) en buyuk dilime (5 puan) eklendi. Beklenen deger
 * 13.332.005 / 1.340.000 = 9,949 puan/cevirme (~70/hafta). Migration `20261013090000_oyun_2b` AYNI icerigi yazar;
 * tutarlilik testi (`oyun-b-saf.test.ts`) ikisini karsilastirir. Oranlar kodda SABIT DEGIL: yonetim yeni surum acar.
 */
export const CARK_TABLOSU_V1: readonly CarkTabloDilimi[] = [
  { puan: 5, agirlik: 804_001 },
  { puan: 10, agirlik: 361_800 },
  { puan: 20, agirlik: 134_000 },
  { puan: 50, agirlik: 38_860 },
  { puan: 500, agirlik: 1_072 },
  { puan: 1000, agirlik: 200 },
  { puan: 5000, agirlik: 67 },
];
export const CARK_TABLOSU_V1_TOPLAM = 1_340_000;
/** Emre 06.10/07.10: son cevirmeden 24 saat sonra bir hak; hak BIRIKMEZ. */
export const CARK_TABLOSU_V1_BEKLEME_SAAT = 24;

// --------------------------------------------------------------------------- cark istekleri

/**
 * Ogrencinin cevirmesi — YALNIZ istek kimligi. Sonucu sunucu belirler (`crypto.randomInt`); ayni kimlikle tekrar
 * istek AYNI sonucu dondurur, ikinci cevirme olmaz (`WheelSpin.requestKey` tekil).
 */
export const carkCevirIstegiSemasi = z.object({ istekKimligi }).strict();
export type CarkCevirIstegi = z.infer<typeof carkCevirIstegiSemasi>;

/**
 * Yeni tablo surumu (`oyun.cark.yonet`, sudo). Tablo DEGISMEZ; degisiklik yeni surumdur ve HEMEN gecerlidir (eski
 * cevirmeler kendi surumunde kalir). `bekleme` (saat) verilmezse etkin surumunku korunur.
 *
 * `oncekiSurum`: yoneticinin EKRANDA GORDUGU etkin surum (kod dersi: onay/yayin istegi gorulen surumu tasir). Sunucu
 * yeni surumu `oncekiSurum + 1` olarak yazar; arada baska bir yonetici surum actiysa birincil anahtar cakisir ve 409
 * doner — eski goruntuye dayanan tablo, araya girenin uzerine sessizce yazilmaz.
 */
export const carkTabloIstegiSemasi = z
  .object({
    oncekiSurum: z.number().int().positive(),
    dilimler: carkDilimleriSemasi,
    bekleme: z.number().int().min(CARK_BEKLEME_SAAT_EN_AZ).max(CARK_BEKLEME_SAAT_EN_COK).optional(),
    gerekce: gerekceSemasi({ enAz: GEREKCE_AGIR_EN_AZ }),
  })
  .strict();
export type CarkTabloIstegi = z.infer<typeof carkTabloIstegiSemasi>;

/** Carki ac/kapat (`Settings.modules.gamificationWheel`; `oyun.cark.yonet`, sudo). */
export const carkDurumIstegiSemasi = z.object({ acik: z.boolean() }).strict();
export type CarkDurumIstegi = z.infer<typeof carkDurumIstegiSemasi>;

// --------------------------------------------------------------------------- cark yanitlari

/** Ogrenciye giden dilim: YALNIZ puan. Agirlik ve olasilik ogrenciye HIC donmez (Emre 07.10.2026). */
export interface CarkDilimi {
  puan: number;
}

/**
 * Sunucunun belirledigi sonuc. Dilim sirasi YOK: ok, o puanli dilimlerden birinin icinde rastgele durur (istemci
 * secer, sonucu degistirmez).
 */
export interface CarkSonucu {
  puan: number;
  /** Puan eklendikten sonraki bakiye (defterin `balanceAfter`i; ekran toplamaz). */
  yeniBakiye: number;
  /** Cevirme ani (ISO). */
  tarih: string;
}

/**
 * 7b kararlari (Emre 07.10.2026): carki YALNIZ `ACTIVE` ogrenci cevirir. Diger hesap durumlarinda (FROZEN, EXPIRED,
 * ONBOARDING, PROSPECT, LEFT, GRADUATED, ARCHIVED) `POST /oyun/cark/cevir` HTTP 409 doner (govde `CarkHesapDurumuHatasi`)
 * ve `GET /oyun/cark` `cevrilemez` alanini doldurur. Mesaj SUNUCUDA (durum basina Turkce sozluk); ekran kendi cumlesini
 * uretmez.
 */
export const CARK_HESAP_DURUMU = "CARK_HESAP_DURUMU" as const;

/** Ogrenci hesap durumu — veritabani `StudentStatus` ile ayni degerler (ACTIVE disindakiler cevirmez). */
export type CarkOgrenciDurumu =
  "PROSPECT" | "ONBOARDING" | "ACTIVE" | "FROZEN" | "EXPIRED" | "LEFT" | "GRADUATED" | "ARCHIVED";

/** Carkin bu hesapta neden cevrilemedigi. Alan adlari arayuzle BAGLAYICI (`kod`, `durum`, `mesaj`). */
export interface CarkCevrilemez {
  kod: typeof CARK_HESAP_DURUMU;
  durum: CarkOgrenciDurumu;
  mesaj: string;
}

/** ACTIVE olmayan ogrencinin cevirme istegi: HTTP 409, govde bu bicimde. */
export type CarkHesapDurumuHatasi = CarkCevrilemez;

/** `GET /oyun/cark`. Cark ya da modul kapaliysa uc 404 doner (`acik` acikken her zaman true). */
export interface CarkDurumu {
  acik: boolean;
  hakVar: boolean;
  /**
   * Sade metin ("yarın 14:20'den sonra"; Europe/Istanbul; `sonrakiHakMetni`). Hak varsa null. CANLI GERI SAYIM VE
   * BILDIRIM YOK (cocuk guvenligi): istemci bu metni zamanlayiciyla guncellemez.
   */
  sonrakiHak: string | null;
  bakiye: number;
  /** Etkin tablonun puanlari (agirlik/olasilik YOK). Dilim duzeni ve boylari istemci tasarimindadir. */
  dilimler: CarkDilimi[];
  /** Son cevirme — yaniti kaybolan cevirmenin sonucunu yeniden gostermek icin. */
  sonCevirme: CarkSonucu | null;
  /**
   * Hesap ACTIVE degilse dolu (o zaman `hakVar: false`, `sonrakiHak: null`); ACTIVE ise null. Tablo ve dilimler yine
   * doner (cark cizilir, dugme kapali).
   */
  cevrilemez: CarkCevrilemez | null;
}

/** Hak yokken `POST /oyun/cark/cevir` yaniti: HTTP 409, govde bu bicimde. */
export const CARK_HAK_YOK = "HAK_YOK" as const;
export interface CarkHakYokHatasi {
  kod: typeof CARK_HAK_YOK;
  mesaj: string;
  sonrakiHak: string | null;
  sonCevirme: CarkSonucu | null;
}

/** Yonetim dilimi: agirlik + sunucuda hesaplanmis olasilik METNI ("%60", "%0,015"). Yalniz `oyun.cark.yonet`. */
export interface CarkYonetimDilimi {
  puan: number;
  agirlik: number;
  olasilik: string;
}

/** `GET /oyun/yonetim/cark/tablolar` — etkin surum. */
export interface CarkYonetimDurumu {
  /** `Settings.modules.gamificationWheel` bayragi (yalniz `false` ise kapali). */
  acik: boolean;
  surum: number;
  /** Cevirme basina beklenen puan, 2 ondalik (sunucu hesaplar). */
  beklenenDeger: number;
  /** Bekleme, SAAT (`cooldownHours`). */
  bekleme: number;
  dilimler: CarkYonetimDilimi[];
}

/** `POST /oyun/yonetim/cark/durum` yaniti. */
export interface CarkDurumYaniti {
  acik: boolean;
}

// =========================================================================== LIDERLIK (F-GAM-17)

/**
 * Ogrenci sorgusu. Varsayilan GECEN HAFTA (S1: kesin tablo). Sinif GONDERILEMEZ: istekte bulunanin
 * `Student.gradeLevel`i sunucuda okunur (IDOR). Bu hafta ve gelecek reddedilir.
 */
export const liderlikSorguSemasi = z.object({ hafta: haftaGunuSemasi.optional() }).strict();
export type LiderlikSorgu = z.infer<typeof liderlikSorguSemasi>;

/**
 * Personel sorgusu. `sinif` verilmezse butun siniflar tek listede (`sinif: null`) — koc kendi ogrencilerini
 * (ASSIGNED) gorur; kapsam sunucuda `scopeOf` ile.
 */
export const yonetimLiderlikSorguSemasi = z
  .object({
    sinif: z.enum(SINIF_DUZEYLERI).optional(),
    hafta: haftaGunuSemasi.optional(),
  })
  .strict();
export type YonetimLiderlikSorgu = z.infer<typeof yonetimLiderlikSorguSemasi>;

/** Satir yalniz bunlari tasir: tam ad, kimlik, fotograf ISTEMCIYE GITMEZ ("Elif Y." sunucuda, `ad-kisalt.ts`). */
export interface LiderlikSatiri {
  /** Esit puan ayni sira (1, 2, 2, 4); esitlerin kendi icinde sirasi ada gore. */
  sira: number;
  ad: string;
  puan: number;
  benMi: boolean;
}

/** `GET /oyun/liderlik`. Toplam `requestedAmount` ile, yalniz `countsForLeaderboard` hareketler (cark/davet YOK). */
export interface Liderlik {
  /** Haftanin Pazartesi'si (`YYYY-AA-GG`). */
  hafta: string;
  /** Gec giris penceresi (kesinlesmeden 48 saat — Carsamba) kapandi mi; degilse ekran "Çarşamba kesinleşir" der. */
  kesin: boolean;
  /** Ilk 10. */
  satirlar: LiderlikSatiri[];
  /** Ilk 10'da degilse kendi siran ve puanin; ilk 10'daysa ya da listede yoksa null. */
  ben: { sira: number; puan: number } | null;
}

export interface YonetimLiderlikSatiri {
  sira: number;
  ogrenciId: string;
  /** Personelde de kisaltilmis ad (tek kural; tam ad ogrenci kartinda). */
  ad: string;
  puan: number;
}

/** `GET /oyun/yonetim/liderlik`. */
export interface YonetimLiderlik {
  hafta: string;
  kesin: boolean;
  sinif: SinifDuzeyi | null;
  satirlar: YonetimLiderlikSatiri[];
}

// =========================================================================== DAVET (F-GAM-18)

/**
 * Davet kodu alfabesi — 32 karakter: karisan 0/O ve 1/I YOK. 32 = 2^5: `crypto.randomInt(32)` sapmasiz. Veritabani
 * CHECK'i (`Invitation_kod_bicimi`) ayni alfabeyi zorlar.
 */
export const DAVET_KODU_ALFABESI = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789";
export const DAVET_KODU_BOYU = 8;

/**
 * Personelin girdigi kod: bosluk ve tire atilir, buyuk harfe cevrilir (`toUpperCase` dilden bagimsiz: "i" -> "I",
 * alfabede olmadigi icin reddedilir — "İ" uretip yanlis kodu kabul etmez).
 */
export const davetKoduSemasi = z
  .string()
  .max(40)
  .transform((s) => s.replace(/[\s​-‍﻿-]/g, "").toUpperCase())
  .pipe(
    z
      .string()
      .regex(
        new RegExp(`^[${DAVET_KODU_ALFABESI}]{${DAVET_KODU_BOYU}}$`),
        `Davet kodu ${DAVET_KODU_BOYU} karakter olmalı (harf ve rakam; 0, O, 1 ve I kullanılmaz).`,
      ),
  );

/**
 * `POST /crm/adaylar/:id/davet-kodu` — satis personeli adayin soyledigi kodu girer (ogrenciden arkadas bilgisi
 * ALINMAZ). Dogrulama sunucuda: kod etkin, aday kaydolmamis, kendi kendini davet yok; kayittan sonra degismez
 * (veritabani tetikleyicisi `aday_davet_kodu_koruma`).
 */
export const adayDavetKoduIstegiSemasi = z.object({ kod: davetKoduSemasi }).strict();
export type AdayDavetKoduIstegi = z.infer<typeof adayDavetKoduIstegiSemasi>;

/** CRM davet kodu yaniti (panel `AdayDavetKodu`). */
export interface AdayDavetKodu {
  /** Girilmis kod; yoksa null. */
  kod: string | null;
}

/**
 * `GET /crm/adaylar/:id` yanitina eklenen alan — ayrinti sayfasi yenilenince kayitli kod gorunsun. Yalniz KOD; davet
 * edenin adi/kimligi DONMEZ.
 */
export interface AdayDavetAlani {
  davetKodu: string | null;
}

/**
 * `GET /oyun/davet` ve `POST /oyun/davet` (idempotent: kod yoksa olusturur, varsa ayni kodu dondurur). Arkadasin
 * adi GOSTERILMEZ (F-GAM-18): yalniz sayilar.
 */
export interface DavetDurumu {
  /** Kod yoksa (henuz `POST` edilmedi) null. */
  kod: string | null;
  /** Kodla gelip ilk gercek odemesini yapan arkadas sayisi (bekleyen + yazilan). */
  ilkOdemeYapan: number;
  /** Ilk odemesi yapilmis ama cayma suresi (`beklemeGun`) dolmamis davetler. */
  beklemede: number;
  /** Davet edene puani yazilmis davetler. */
  puanYazilan: number;
  /** Etkin kural surumunden; kural yoksa null (ekran miktar yazmaz — koda gomulu degil). */
  davetEdenPuan: number | null;
  davetEdilenPuan: number | null;
  beklemeGun: number | null;
}
